import dayjs from "dayjs";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { getCustomerAccountTeams } from "../../../api/customer-service.ts";
import { getServiceFailures } from "../../../api/service-failures.api.ts";
import useAccountEnhanced from "../../../hooks/use-account-enhanced.ts";
import { CustomerAccountTeam } from "../../../types/customer-account-team-management/customer-account-team.ts";
import { ServiceFailureRecord } from "../../../types/customer-service-failures/service-failure";

export default function ServiceFailures() {
    const accountEnhanced = useAccountEnhanced();
    const [teams, setTeams] = useState<CustomerAccountTeam[]>([]);
    const [SFList, setSFList] = useState<ServiceFailureRecord[]>([]);
    const [statusBoxes, setStatusBoxes] = useState({
        pending: false,
        draft: false,
        csrReview: true,
        finalReview: false,
        rejected: false,
        approved: false,
    });
    const [status, setStatus] = useState("CSR Review");
    const [csrCode, setCsrCode] = useState("");
    const [zoneName, setSelectedZone] = useState("");
    const [orderNum, setOrderNum] = useState("");
    const [pageNumber, setPageNumber] = useState(0);
    const pageSize = 20;

    const navigate = useNavigate();

    useEffect(() => {
        const checkedBoxes = Object.keys(statusBoxes)
            .filter((box) => statusBoxes[box as keyof typeof statusBoxes])
            .map((box) => {
                switch (box) {
                    case "pending":
                        return "Pending";
                    case "draft":
                        return "Draft";
                    case "csrReview":
                        return "CSR Review";
                    case "finalReview":
                        return "Final Review";
                    case "rejected":
                        return "Rejected";
                    case "approved":
                        return "Approved";
                    default:
                        return "";
                }
            });
        setStatus(checkedBoxes.join(","));
    }, [statusBoxes, accountEnhanced]);

    const fetchData = async () => {
        try {
            handleGetTeams();
            handleGetServiceFailures(0);
        } catch (error) {
            console.error("Error getting service failures: ", error);
        }
    };

    const handleGetTeams = async () => {
        try {
            const customerAccountTeams = await getCustomerAccountTeams();
            setTeams(customerAccountTeams);
        } catch (error) {
            toast.error(
                error instanceof Error ? error.message : "An error occurred",
            );
        }
    };

    const handleStatusBoxChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const { name, checked } = event.target;
        setStatusBoxes((prevState) => ({
            ...prevState,
            [name]: checked,
        }));
        setStatus("");
    };

    const handleUserCodeChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setCsrCode(event.target.value);
    };

    const handleZoneChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const zoneName = event.target.value;
        setSelectedZone(zoneName);

        const selectedTeam = teams.find((team) => team.TeamName === zoneName);

        if (selectedTeam) {
            const codes = selectedTeam.Members.map(
                (member) => member.EmployeeCode,
            ).join(", ");
            setCsrCode(codes);
        } else {
            setCsrCode(
                import.meta.env.MODE === "development"
                    ? "ARMEL"
                    : accountEnhanced.me.employeeCode?.toUpperCase() ?? "",
            );
        }
    };

    // service failures
    const handleGetServiceFailures = async (pgNumber: number) => {
        try {
            let startDate = dayjs().subtract(1, "month");
            const params: { [key: string]: string } = {};
            if (status) {
                params.status = status;
            }
            params.csrCode = csrCode;
            params.page = pgNumber.toString();

            if (import.meta.env.VITE_DEPLOYMENT === "DEV") {
                startDate = startDate.subtract(6, "year");
                params.endDate = startDate.add(1, "year").format("YYYY-MM-DD");
            }
            params.startDate = startDate.format("YYYY-MM-DD");
            const queryString = new URLSearchParams(params).toString();

            const serviceFailures = await getServiceFailures(queryString);

            // [QueryStringParameter("status", "Status filter (Pending|Submitted|ApprovedCSM|Complete|Rejected).", DataType = typeof(string), Required = false)] (comma separated)
            // [QueryStringParameter("csrCode", "Filter by zero or more CSR codes.", DataType = typeof(string), Required = false)] (comma separated)
            // [QueryStringParameter("startDate", "The earliest order date to be included.", DataType = typeof(DateTime), Required = false)]
            // [QueryStringParameter("endDate", "The latest order date to be included.", DataType = typeof(DateTime), Required = false)]
            // [QueryStringParameter("page", "Specifies what data page to return. Default values is 0.", DataType = typeof(int), Required = false)]
            // [QueryStringParameter("pageSize", "Specifies how many records to return per page. Default value is 20.", DataType = typeof(int), Required = false)]
            setSFList(serviceFailures);
        } catch (error: unknown) {
            toast.error(
                error instanceof Error ? error.message : "An error occurred",
            );
        }
    };

    const handleSearchOrders = async () => {
        setPageNumber(0);
        handleGetServiceFailures(0);
    };

    const handleOrderChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setOrderNum(event.target.value);
    };

    const handleFindOrder = async () => {
        if (orderNum) {
			navigate(`./${orderNum}`);
        }
    };

    function handleMoreOrders(): void {
        setPageNumber(pageNumber + 1);
        handleGetServiceFailures(pageNumber + 1);
    }

    useEffect(() => {
        setCsrCode((prevState) =>
            prevState !== ""
                ? prevState
                : import.meta.env.MODE === "development"
                ? "ARMEL"
                : accountEnhanced.me.employeeCode?.toUpperCase() ?? "",
        );
    }, [accountEnhanced.me.employeeCode]);

    useEffect(() => {
        if (csrCode) {
            fetchData();
        }
    }, [csrCode]);
    return (
        <>
            <div className="bg-gray-800 text-gray-900 min-h-full p-8">
                <div className="flex space-x-4">
                    {/* Left Column: Form */}
                    <div className="w-1/3 p-4 space-y-4 bg-gray-700 shadow-lg rounded-lg">
                        <div className="flex">
                            <fieldset className="fieldset mr-2 flex-1">
                                <label className="label">
                                    <span className="text-gray-50">
                                        Order Number
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={orderNum}
                                    placeholder="OrderNum"
                                    onChange={handleOrderChange}
                                />
                            </fieldset>
                            <button
                                type="button"
                                onClick={handleFindOrder}
                                className="btn btn-secondary self-end"
                            >
                                Find
                            </button>
                        </div>
                        <div>
                            <fieldset className="fieldset">
                                <label className="label">
                                    <span className="text-gray-50">Zone</span>
                                </label>
                                <select
                                    id="ddlZone"
                                    value={zoneName}
                                    onChange={handleZoneChange}
                                    className="select select-bordered"
                                >
                                    <option value="">
                                        -- Select a Zone --
                                    </option>
                                    {teams.map((team) => (
                                        <option
                                            key={team.TeamID}
                                            value={team.TeamName}
                                        >
                                            {team.TeamName}
                                        </option>
                                    ))}
                                </select>
                            </fieldset>
                        </div>
                        <div>
                            {/* User Code */}
                            <fieldset className="fieldset w-full col-span-12">
                                <label className="label">
                                    <span className="text-gray-50">
                                        User Code
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered"
                                    value={csrCode}
                                    onChange={handleUserCodeChange}
                                />
                            </fieldset>
                        </div>
                        <div className="grid grid-cols-2 gap-x-14">
                            {/* Status */}
                            <fieldset className="fieldset">
                                <label className="label cursor-pointer">
                                    <span className="text-gray-50">
                                        Pending
                                    </span>
                                    <input
                                        type="checkbox"
                                        name="pending"
                                        className="ml-auto checkbox checkbox-secondary"
                                        checked={statusBoxes.pending}
                                        onChange={handleStatusBoxChange}
                                    />
                                </label>
                            </fieldset>

                            <fieldset className="fieldset">
                                <label className="label cursor-pointer">
                                    <span className="text-gray-50">Draft</span>
                                    <input
                                        type="checkbox"
                                        name="draft"
                                        className="ml-auto checkbox checkbox-secondary"
                                        checked={statusBoxes.draft}
                                        onChange={handleStatusBoxChange}
                                    />
                                </label>
                            </fieldset>

                            <fieldset className="fieldset">
                                <label className="label cursor-pointer">
                                    <span className="text-gray-50">
                                        CSR Review
                                    </span>
                                    <input
                                        type="checkbox"
                                        name="csrReview"
                                        className="ml-auto checkbox checkbox-secondary"
                                        checked={statusBoxes.csrReview}
                                        onChange={handleStatusBoxChange}
                                    />
                                </label>
                            </fieldset>

                            <fieldset className="fieldset">
                                <label className="label cursor-pointer">
                                    <span className="text-gray-50">
                                        Final Review
                                    </span>
                                    <input
                                        type="checkbox"
                                        name="finalReview"
                                        className="ml-auto checkbox checkbox-secondary"
                                        checked={statusBoxes.finalReview}
                                        onChange={handleStatusBoxChange}
                                    />
                                </label>
                            </fieldset>

                            <fieldset className="fieldset">
                                <label className="label cursor-pointer">
                                    <span className="text-gray-50">
                                        Rejected
                                    </span>
                                    <input
                                        type="checkbox"
                                        name="rejected"
                                        className="ml-auto checkbox checkbox-secondary"
                                        checked={statusBoxes.rejected}
                                        onChange={handleStatusBoxChange}
                                    />
                                </label>
                            </fieldset>

                            <fieldset className="fieldset">
                                <label className="label cursor-pointer">
                                    <span className="text-gray-50">
                                        Approved
                                    </span>
                                    <input
                                        type="checkbox"
                                        name="approved"
                                        className="ml-auto checkbox checkbox-secondary"
                                        checked={statusBoxes.approved}
                                        onChange={handleStatusBoxChange}
                                    />
                                </label>
                            </fieldset>
                        </div>
                        <div className=" w-full ">
                            <button
                                type="button"
                                onClick={() => handleSearchOrders()}
                                className="btn btn-primary"
                            >
                                Search
                            </button>
                        </div>
                    </div>
                    <div className=" w-full col-span-2">
                        {SFList && (
                            <div className="bg-gray-700 card card-compact">
                                <div className="card-body">
                                    <div className="overflow-y-auto h-96">
                                        <table className="table table-pin-rows text-gray-50">
                                            <thead>
                                                <tr className="text-white bg-gray-600">
                                                    <th>Order</th>
                                                    <th>Bill to</th>
                                                    <th>Order Status</th>
                                                    <th>Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {SFList.map((item) => (
                                                    <tr
                                                        key={
                                                            item.Order
                                                                .OrderNumber
                                                        }
                                                        className=""
                                                    >
                                                        <td className="font-semibold">
                                                            <Link
                                                                to={
                                                                    item.Order
                                                                        .OrderNumber
                                                                }
                                                            >
                                                                {
                                                                    item.Order
                                                                        .OrderNumber
                                                                }
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            {
                                                                item.Order
                                                                    .BillToCustomerCode
                                                            }
                                                        </td>
                                                        <td>
                                                            {item.Order.Status}
                                                        </td>
                                                        <td>
                                                            {item.ServiceFailure
                                                                .ServiceFailureType +
                                                                ", " +
                                                                item.Order.CSREmployeeCode?.trim() +
                                                                " - " +
                                                                item
                                                                    .ServiceFailure
                                                                    .Status +
                                                                (item
                                                                    .ServiceFailure
                                                                    .CSM_Approval
                                                                    .ApprovalStatus ===
                                                                "Approved"
                                                                    ? ", Approved by CSM"
                                                                    : "")}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                        {SFList.length === pageSize && (
                            <div
                                style={{
                                    textAlign: "right",
                                    paddingRight: "20px",
                                    paddingTop: "10px",
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => handleMoreOrders()}
                                    className="btn btn-sm btn-secondary"
                                >
                                    more
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
